import { useRef, useMemo, useCallback } from "react"
import type { LinkProps, Location } from "react-router-dom"
import {
  useNavigate as useNavigateLib,
  useLocation as useLocationLib,
} from "react-router-dom"

/**
 * Returns true if `to` is a partial URL using the special update syntax:
 * - `?key=value`   → replace/set search params, keep pathname and hash
 * - `+?key=value`  → append search params (multi-value), keep pathname and hash
 * - `>>?key=value` → push value onto a comma-separated param, keep pathname and hash
 * - `#hash`        → update hash only, keep pathname and search
 */
export function isPartialHref(to: string): boolean {
  return (
    to.startsWith("?") ||
    to.startsWith("+?") ||
    to.startsWith(">>?") ||
    to.startsWith("#")
  )
}

/**
 * Resolves a partial href into a full URL by merging with the current location.
 *
 * - `?key=value`   → keep pathname + hash, merge/replace these search params into current search
 * - `+?key=value`  → keep pathname + hash, append these search params (multi-value) to current search
 * - `>>?key=value` → keep pathname + hash, push value onto the end of a comma-separated param
 * - `#hash`        → keep pathname + search, update hash
 */
export function resolvePartialHref(to: string, location: Location): string {
  const { pathname, search, hash } = location
  const currentSearch = search.startsWith("?") ? search.slice(1) : search

  if (to.startsWith(">>?")) {
    // Same URL behaviour as `?` — the "push to history stack" signal is carried
    // via navigation state in Link.tsx, not encoded in the URL.
    const newParams = new URLSearchParams(to.slice(3))
    const merged = new URLSearchParams(currentSearch)
    for (const [key, value] of newParams) {
      merged.set(key, value)
    }
    const q = merged.toString()
    return `${pathname}${q ? `?${q}` : ""}${hash}`
  }

  if (to.startsWith("+?")) {
    const newParams = new URLSearchParams(to.slice(2))
    const merged = new URLSearchParams(currentSearch)
    for (const [key, value] of newParams) {
      merged.append(key, value)
    }
    const q = merged.toString()
    return `${pathname}${q ? `?${q}` : ""}${hash}`
  }

  if (to.startsWith("?")) {
    const newParams = new URLSearchParams(to.slice(1))
    const merged = new URLSearchParams(currentSearch)
    for (const [key, value] of newParams) {
      merged.set(key, value)
    }
    const q = merged.toString()
    return `${pathname}${q ? `?${q}` : ""}${hash}`
  }

  if (to.startsWith("#")) {
    return `${pathname}${search}${to}`
  }

  return to
}

export function useLocation<T = never>() {
  const location = useLocationLib()
  return location as Omit<Location, "state"> & { state: T }
}

type NavigateOptions = {
  replace?: boolean
  state?: any
}

const DEFAULT_NAV_OPTIONS: NavigateOptions = {
  replace: false,
}

export function useNavigate() {
  const navigate = useNavigateLib()
  return useCallback(
    (
      to: LinkProps["to"],
      options = DEFAULT_NAV_OPTIONS,
      keepParams = true,
      keepHash = true,
      keepState = false,
    ) => {
      if (keepParams) to += window.location.search
      if (keepHash) to += window.location.hash
      if (keepState) {
        const currentState = window.history.state?.usr || {}
        options = {
          ...options,
          state: { ...currentState, ...options.state },
        }
      }
      options ? navigate(to, options) : navigate(to)
    },
    [navigate],
  )
}

type ParamKeyValuePair = [string, string]
export type URLSearchParamsInit =
  | string
  | ParamKeyValuePair[]
  | Record<string, string | string[] | null>
  | URLSearchParams

/**
 * Creates a URLSearchParams object using the given initializer.
 *
 * This is identical to `new URLSearchParams(init)` except it also
 * supports arrays as values in the object form of the initializer
 * instead of just strings. This is convenient when you need multiple
 * values for a given key, but don't want to use an array initializer.
 *
 * For example, instead of:
 *
 *   let searchParams = new URLSearchParams([
 *     ['sort', 'name'],
 *     ['sort', 'price']
 *   ])
 *
 * you can do:
 *
 *   let searchParams = createSearchParams({
 *     sort: ['name', 'price']
 *   })
 */
function createSearchParams(init: URLSearchParamsInit = "") {
  if (
    typeof init === "string" ||
    Array.isArray(init) ||
    init instanceof URLSearchParams
  ) {
    return new URLSearchParams(init)
  }

  const params: ParamKeyValuePair[] = []

  for (const key of Object.keys(init)) {
    const values = init[key]
    if (!values) continue

    if (Array.isArray(values)) {
      for (const value of values) {
        params.push([key, value])
      }
    } else {
      params.push([key, values])
    }
  }

  return new URLSearchParams(params)
}

/**
 * A convenient wrapper for reading and writing search parameters via the
 * URLSearchParams interface.
 */
export function useSearchParams(defaultInit?: URLSearchParamsInit) {
  const defaultSearchParamsRef = useRef(createSearchParams(defaultInit))

  const location = useLocation()
  const searchParams = useMemo(() => {
    const searchParams = createSearchParams(location.search)

    for (const key of defaultSearchParamsRef.current.keys()) {
      if (!searchParams.has(key)) {
        for (const value of defaultSearchParamsRef.current.getAll(key)) {
          searchParams.append(key, value)
        }
      }
    }

    return searchParams
  }, [location.search])

  const navigate = useNavigate()
  const setSearchParams = useCallback(
    (
      nextInit: URLSearchParamsInit,
      navigateOptions?: { replace?: boolean; state?: any },
    ) => {
      if (typeof nextInit === "object") {
        nextInit = {
          ...Object.fromEntries(searchParams.entries()),
          ...nextInit,
        }
      }

      const params = createSearchParams(nextInit)
      navigate(
        `${window.location.pathname}?${params}`,
        navigateOptions,
        false,
        true,
        true,
      )
    },
    [navigate, searchParams],
  )

  return [searchParams, setSearchParams] as const
}

export type ExtraParams = Record<string, string>
export type NavOptions = {
  replace?: boolean
  state?: object
}

export function useSearchParamOpener(key: string) {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeValue = searchParams.get(key)

  function getIsActive(value: string | number) {
    return activeValue === String(value)
  }

  const open = useCallback(
    (
      value: string | number = "1",
      extra?: ExtraParams,
      options?: NavOptions,
    ) => {
      setSearchParams(
        {
          [key]: String(value),
          ...extra,
        },
        options,
      )
    },
    [key, setSearchParams],
  )

  const close = useCallback(
    (extra?: ExtraParams) => {
      setSearchParams({
        [key]: null,
        ...extra,
      })
    },
    [key, setSearchParams],
  )

  const toggle = useCallback(
    (
      value = searchParams.get(key),
      extra?: ExtraParams,
      options?: NavOptions,
    ) => {
      const isActive = value === searchParams.get(key)
      isActive ? close(extra) : open(value ?? "1", extra, options)
      return !isActive
    },
    [key, searchParams, open, close],
  )

  return {
    searchParams,
    setSearchParams,
    isActive: !!activeValue,
    activeValue,
    getIsActive,
    open,
    close,
    toggle,
  }
}

export function parseEmails(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0 && s.includes("@"))
}
